import { useEffect, useMemo, useState } from "react";

import SearchIcon from "@mui/icons-material/Search";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  TablePagination,
  TextField,
  Typography,
} from "@mui/material";

import type { MessagingPatient } from "../types";

type Props = {
  patients: MessagingPatient[];
  isSearching: boolean;
  hasSearchError: boolean;
  selectedPatients: MessagingPatient[];
  onSelectedPatientsChange: (patients: MessagingPatient[]) => void;
};

function isPatientSelected(
  selectedPatients: MessagingPatient[],
  patient: MessagingPatient,
): boolean {
  return selectedPatients.some((p) => p.id === patient.id);
}

export function PatientSelectionList({
  patients,
  isSearching,
  hasSearchError,
  selectedPatients,
  onSelectedPatientsChange,
}: Props) {
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    setPage(0);
  }, [searchTerm]);

  const areAllPatientsSelected = useMemo(() => {
    if (patients.length === 0) return false;
    return patients.every((patient) => isPatientSelected(selectedPatients, patient));
  }, [patients, selectedPatients]);

  const filteredPatients = useMemo(() => {
    const term = String(searchTerm ?? "").trim().toLowerCase();
    if (!term) return patients;
    return patients.filter((patient) => {
      const blob = [patient.fullName, patient.cpf, patient.phone]
        .map((s) => String(s ?? "").toLowerCase())
        .join(" ");
      return blob.includes(term);
    });
  }, [patients, searchTerm]);

  const paginatedPatients = useMemo(() => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredPatients.slice(startIndex, endIndex);
  }, [filteredPatients, page, rowsPerPage]);

  const handleTogglePatient = (patient: MessagingPatient) => {
    const alreadySelected = isPatientSelected(selectedPatients, patient);
    if (alreadySelected) {
      onSelectedPatientsChange(selectedPatients.filter((p) => p.id !== patient.id));
      return;
    }
    onSelectedPatientsChange([...selectedPatients, patient]);
  };

  const handleSelectAllClick = () => {
    if (areAllPatientsSelected) {
      onSelectedPatientsChange([]);
      return;
    }
    onSelectedPatientsChange(patients);
  };

  return (
    <Stack spacing={2}>
      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          borderRadius: 2,
          bgcolor: "background.paper",
        }}
      >
        <Stack spacing={1.25}>
          <TextField
            value={searchTerm}
            onChange={(e) => {
              const next = e.target.value;
              setSearchTerm(next);
            }}
            placeholder="Digite o nome do paciente..."
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
              ),
              endAdornment: isSearching ? (
                <CircularProgress size={18} color="inherit" />
              ) : null,
            }}
          />

          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={1}
          >
            <Typography variant="body2" color="text.secondary">
              {filteredPatients.length} paciente
              {filteredPatients.length === 1 ? "" : "s"} na lista
            </Typography>

            <Button
              variant="outlined"
              size="small"
              sx={{ textTransform: "none" }}
              onClick={handleSelectAllClick}
              disabled={patients.length === 0}
            >
              {areAllPatientsSelected ? "Limpar seleção" : "Selecionar todos"}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {hasSearchError && (
        <Paper
          variant="outlined"
          sx={{ p: 1.5, borderRadius: 2, bgcolor: "background.paper" }}
        >
          <Typography variant="body2" color="error">
            Erro ao buscar pacientes. Tente novamente.
          </Typography>
        </Paper>
      )}

      {!hasSearchError && patients.length === 0 && !isSearching && (
        <Paper
          variant="outlined"
          sx={{ p: 1.5, borderRadius: 2, bgcolor: "background.paper" }}
        >
          <Typography variant="body2" color="text.secondary">
            Nenhum paciente encontrado.
          </Typography>
        </Paper>
      )}

      {!hasSearchError && patients.length > 0 && filteredPatients.length === 0 && !isSearching && (
        <Paper
          variant="outlined"
          sx={{ p: 1.5, borderRadius: 2, bgcolor: "background.paper" }}
        >
          <Typography variant="body2" color="text.secondary">
            Nenhum paciente encontrado para este filtro.
          </Typography>
        </Paper>
      )}

      <Paper
        variant="outlined"
        sx={{
          borderRadius: 2,
          bgcolor: "background.paper",
          overflow: "hidden",
        }}
      >
        <Stack
          spacing={1}
          sx={{
            p: 1.25,
            maxHeight: 360,
            overflowY: "auto",
            "&::-webkit-scrollbar": { width: "8px" },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "rgba(0, 0, 0, 0.05)",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(0, 0, 0, 0.2)",
              borderRadius: "4px",
              "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.3)" },
            },
          }}
        >
          {paginatedPatients.map((patient) => {
            const selected = isPatientSelected(selectedPatients, patient);
            return (
              <Paper
                key={patient.id}
                elevation={0}
                sx={{
                  px: 2,
                  py: 1,
                  border: "1px solid",
                  borderColor: selected ? "primary.main" : "divider",
                  borderRadius: 1.5,
                  backgroundColor: selected
                    ? "rgba(25, 118, 210, 0.06)"
                    : "background.paper",
                  transition: "all 0.2s ease",
                  cursor: "pointer",
                  position: "relative",
                  "&:hover": {
                    borderColor: "primary.main",
                    backgroundColor: "rgba(25, 118, 210, 0.06)",
                    transform: "translateX(4px)",
                    boxShadow: 1,
                  },
                  ...(selected && {
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: "4px",
                      backgroundColor: "primary.main",
                      borderRadius: "4px 0 0 4px",
                    },
                  }),
                }}
                onClick={() => handleTogglePatient(patient)}
                role="button"
                aria-label={`Selecionar paciente ${patient.fullName}`}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    {selected ? (
                      <CheckBoxIcon sx={{ fontSize: 22, color: "primary.main" }} />
                    ) : (
                      <CheckBoxOutlineBlankIcon
                        sx={{ fontSize: 22, color: "text.secondary" }}
                      />
                    )}
                  </Box>

                  <Stack sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      noWrap
                      sx={{ color: selected ? "primary.main" : "text.primary" }}
                    >
                      {patient.fullName}
                    </Typography>

                    {(patient.phone || patient.cpf) && (
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {[patient.phone, patient.cpf].filter(Boolean).join(" · ")}
                      </Typography>
                    )}
                  </Stack>
                </Stack>
              </Paper>
            );
          })}
        </Stack>

        <Divider />

        <TablePagination
          component="div"
          count={filteredPatients.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Itens por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          sx={{ px: 1 }}
        />
      </Paper>
    </Stack>
  );
}

